// The reel inside the recipe page: the cover with a play button, then the video playing in the
// same place, streamed straight from Instagram's servers (the API only looks up the address;
// nothing is stored). While playing it stays pinned at the top so the ingredients and steps
// scroll underneath; Full screen enlarges it and Close goes back to the cover. If the video
// cannot be streamed, Instagram's own player is shown instead.
import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api';
import { Thumb } from './Thumb';
import { Close, Expand, Play } from '../icons';
import { useT } from '../i18n';

/** Instagram's embeddable player for a reel shortcode (only real shortcodes, not "ig_<media id>"). */
export function embedURL(shortcode: string): string {
  return /^[A-Za-z0-9_-]{5,40}$/.test(shortcode) && !shortcode.startsWith('ig_')
    ? `https://www.instagram.com/reel/${shortcode}/embed/`
    : '';
}

/** The video address, asking again while the server is still looking it up (slow on the phone). */
async function videoAddress(id: number, signal: { stop: boolean }): Promise<string> {
  for (let i = 0; i < 40 && !signal.stop; i++) {
    const v = await api.reelVideo(id);
    if (v.url) return v.url;
    await new Promise((r) => setTimeout(r, 2500));
  }
  throw new Error('video lookup timed out');
}

type State = { kind: 'cover' } | { kind: 'loading' } | { kind: 'video'; src: string } | { kind: 'embed' };

export function ReelPlayer({ id, shortcode, cover, name }: { id: number; shortcode: string; cover?: string; name: string }) {
  const t = useT();
  const [st, setSt] = useState<State>({ kind: 'cover' });
  const retried = useRef(false);
  const box = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const embed = embedURL(shortcode);
  const run = useRef({ stop: false });
  // Start the lookup as the page opens so Play is quick; the server caches the address for hours.
  useEffect(() => {
    if (embed) api.reelVideo(id).catch(() => undefined);
    return () => { run.current.stop = true; };
  }, [id]);

  if (!embed) return <div style="padding:6px 16px 0"><Thumb src={cover} name={name} size="hero" /></div>;

  const load = async () => {
    setSt({ kind: 'loading' });
    run.current = { stop: false };
    const signal = run.current;
    try {
      const src = await videoAddress(id, signal);
      if (!signal.stop) setSt({ kind: 'video', src });
    } catch {
      if (!signal.stop) setSt({ kind: 'embed' });
    }
  };
  // An address can expire while the page sits open: fetch a fresh one once, then give up to the embed.
  const onError = () => {
    if (retried.current) { setSt({ kind: 'embed' }); return; }
    retried.current = true;
    void load();
  };

  if (st.kind === 'cover' || st.kind === 'loading') {
    const busy = st.kind === 'loading';
    return (
      <div style="padding:6px 16px 0">
        <button class="reel-cover" onClick={load} disabled={busy} aria-busy={busy} aria-label={t('recipe.playReel')}>
          <Thumb src={cover} name={name} size="hero" />
          <span class={'reel-play' + (busy ? ' busy' : '')} aria-hidden="true">{busy ? <span class="reel-spin" /> : <Play size={30} />}</span>
        </button>
      </div>
    );
  }

  const full = () => {
    const v = video.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (document.fullscreenElement) { void document.exitFullscreen(); return; }
    if (v?.requestFullscreen) void v.requestFullscreen();
    else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iOS Safari
    else void box.current?.requestFullscreen?.();
  };
  const close = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    video.current?.pause();
    run.current.stop = true;
    retried.current = false;
    setSt({ kind: 'cover' });
  };
  return (
    <div class="reel-dock">
      <div class={'reel-frame' + (st.kind === 'embed' ? ' embed' : '')} ref={box}>
        {st.kind === 'video'
          ? <video ref={video} src={st.src} poster={cover} autoplay playsInline controls preload="auto" onError={onError} aria-label={name} />
          : <iframe src={embed} title={name} allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen
              referrerpolicy="strict-origin-when-cross-origin" scrolling="no" />}
        <div class="reel-tools">
          {st.kind === 'video' && <button onClick={full} aria-label={t('recipe.fullScreen')}><Expand /></button>}
          <button onClick={close} aria-label={t('recipe.closeReel')}><Close /></button>
        </div>
      </div>
    </div>
  );
}
